import React, { FC, useMemo } from 'react'
import { useRouter } from 'next/router'
import { FlexColumn } from '@/atoms/FlexContainers'
import { slugs } from '@/constants'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { vacationSlugs } from '@/views/PreSales/PreSalesGiveawayView/PreSalesGiveawayView'
import { GiveawayLogo } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayLogo'
import {
  GFooterMD,
  GResponsiveText,
  GTitleSM,
  GTitleXS,
} from '@/views/PreSales/PreSalesGiveawayView/components/Typography'
import { GiveawayTab, GiveawayTabEnum } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

interface GiveawayFooterProps {
  scrollToTab: (tab: GiveawayTabEnum) => void
  scrollToProducts: () => void
  logoUrl: string
  theatricalSlug: string
  tabs: GiveawayTab[]
  isActive: boolean
}

const hiddenTabsOnInActive = new Set([
  GiveawayTabEnum.Details,
  GiveawayTabEnum.Updates,
  GiveawayTabEnum.VacationPackages,
])
export const GiveawayFooter: FC<GiveawayFooterProps> = ({
  scrollToTab,
  scrollToProducts,
  logoUrl,
  tabs,
  isActive,
  theatricalSlug,
}) => {
  const { t } = useTranslate('tickets')
  const { push } = useRouter()

  const siteNavigation = useMemo(() => {
    const tabNavigation = tabs
      .map((tab) => {
        return {
          name: tab.title,
          onClick: () => scrollToTab(tab.type),
          type: tab.type,
        }
      })
      .filter((tab) => {
        if (isActive) return true
        return !hiddenTabsOnInActive.has(tab.type)
      })

    return [
      {
        name: isActive
          ? t('preOrderTicketsFooterlink', 'Pre-Order Tickets')
          : t('getShowtimesGiveaway', 'Get Showtimes'),
        onClick: () => (isActive ? scrollToProducts() : push(`/tickets/${theatricalSlug}`)),
      },
      ...tabNavigation,
    ]
  }, [isActive, push, scrollToProducts, scrollToTab, t, tabs, theatricalSlug])

  return (
    <footer className="bg-giveaway-primary-accent px-4 pb-[120px] pt-[72px] md:px-[88px] md:py-[72px]">
      <div className="mx-auto flex h-full w-full max-w-[1440px] flex-col items-start gap-10 lg:flex-row lg:justify-between xl:gap-[120px]">
        <GiveawayLogo isFooter theatricalSlug={theatricalSlug} logoUrl={logoUrl} />

        <FlexColumn className="gap-2 md:hidden md:gap-3">
          <GResponsiveText
            className="!font-medium"
            color="white"
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
          >
            {t('giveawayRules', 'Giveaway Rules')}
          </GResponsiveText>

          {theatricalSlug === slugs.theLastRodeo && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="lastrodeoSweepstakesGeneralRulesFooter">
                * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
                NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The Last Rodeo
                Giveaway is open only to legal residents of the 48 contiguous US States & D.C. (excluding HI and AK),
                age of majority or older. Void elsewhere and where prohibited. Ends 6/7/2025. Subject to complete
                Official Rules at{' '}
                <a target="_blank" className="underline" href="https://bit.ly/TheLastRodeo">
                  https://bit.ly/TheLastRodeo
                </a>
                .
              </Translate>
            </GFooterMD>
          )}

          {theatricalSlug === slugs.kingOfKings && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="tkokSweepstakesGeneralRulesFooter">
                * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
                NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The King of
                Kings Giveaway is open only to legal residents of the 50 US States &amp; D.C., age of majority or older.
                Void elsewhere and where prohibited. Ends 4/27/2025. Odds of winning a prize depend on the number of
                eligible entries received. Subject to complete Official Rules at{' '}
                <a target="_blank" className="underline" href="https://bit.ly/AngelFilmsKingofKings">
                  https://bit.ly/AngelFilmsKingofKings
                </a>
                .
              </Translate>
            </GFooterMD>
          )}

          {theatricalSlug === 'rule-breakers-tech' && (
            <GFooterMD color="white">
              * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
              NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The Rule Breakers
              Tech Giveaway is open only to legal residents of the 50 US States & DC, age of majority +. Void elsewhere
              and where prohibited. Ends 03/09/2025. Subject to complete Official Rules at{' '}
              <a target="_blank" className="underline" href="https://bit.ly/RuleBreakersTech">
                https://bit.ly/RuleBreakersTech
              </a>
              .
            </GFooterMD>
          )}

          {vacationSlugs.has(theatricalSlug) && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="sweepstakesGeneralRulesBraveV3">
                * * * LIMIT OF FIVE THOUSAND (5,000) ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF
                ANY KIND IS NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The
                200K Dream Vacation Giveaway is open only to legal residents of the 50 U.S. States &amp; DC, age of
                majority +. Void elsewhere and where prohibited. Promotion ends 3/9/2025. Subject to complete Official
                Rules at https://bit.ly/200KDreamGiveaway.{' '}
                <a href="https://bit.ly/200KDreamGiveaway" target="_blank" className="underline">
                  https://bit.ly/200KDreamGiveaway
                </a>
              </Translate>
            </GFooterMD>
          )}

          {theatricalSlug === slugs.homestead && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="homesteadSweepstakesGeneralRulesHomesteadV3">
                * * * LIMIT OF FOURTEEN THOUSAND ONE HUNDRED (14,100) ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO
                PURCHASE OR PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR
                CHANCE OF WINNING. The 300K Instant Homestead Giveaway is open only to legal residents of the 48
                Contiguous U.S. States (VOID in AK and HI) & DC, 21 or older. Void elsewhere and where prohibited..
                Promotion starts 10/11/24 and ends 12/22/24. To enter, visit https://angel.com/homesteadgiveaway .
                Sponsor: Angel Studios, 295 W Center Street, Provo, UT 84601.. Subject to complete Official Rules at{' '}
                <a href="https://bit.ly/HomesteadSweeps" target="_blank" className="underline">
                  https://bit.ly/HomesteadSweeps
                </a>
              </Translate>
            </GFooterMD>
          )}
        </FlexColumn>

        <div className="shrink-0">
          <GResponsiveText
            className="!font-medium"
            color="white"
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
          >
            {t('siteNavigation', 'Site Navigation')}
          </GResponsiveText>

          <ul className="mt-2 flex flex-col gap-1 md:mt-3">
            {siteNavigation.map((item) => {
              return (
                <li className="cursor-pointer" onClick={() => item.onClick()} key={item.name}>
                  <GFooterMD color="white" className="underline">
                    {item.name}
                  </GFooterMD>
                </li>
              )
            })}
          </ul>

          <GFooterMD className="mt-10 block" color="giveaway-primary-accent-light">
            <Translate t={t} i18nKey="footerCopyright">
              © 2025 Angel Studios, LLC. All rights reserved.
            </Translate>
          </GFooterMD>
        </div>

        <FlexColumn className="hidden gap-2 md:flex md:gap-3">
          <GResponsiveText
            className="!font-medium"
            color="white"
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
          >
            {t('giveawayRules', 'Giveaway Rules')}
          </GResponsiveText>

          {theatricalSlug === slugs.theLastRodeo && (
            <GFooterMD color="white">
              * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
              NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The Last Rodeo
              Giveaway is open only to legal residents of the 48 contiguous US States & D.C. (excluding HI and AK), age
              of majority or older. Void elsewhere and where prohibited. Ends 6/7/2025. Subject to complete Official
              Rules at{' '}
              <a target="_blank" className="underline" href="https://bit.ly/TheLastRodeo">
                https://bit.ly/TheLastRodeo
              </a>
              .
            </GFooterMD>
          )}

          {theatricalSlug === slugs.kingOfKings && (
            <GFooterMD color="white">
              * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
              NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The King of Kings
              Giveaway is open only to legal residents of the 50 US States &amp; D.C., age of majority or older. Void
              elsewhere and where prohibited. Ends 4/27/2025. Odds of winning a prize depend on the number of eligible
              entries received. Subject to complete Official Rules at{' '}
              <a target="_blank" className="underline" href="https://bit.ly/AngelFilmsKingofKings">
                https://bit.ly/AngelFilmsKingofKings
              </a>
              .
            </GFooterMD>
          )}

          {theatricalSlug === 'rule-breakers-tech' && (
            <GFooterMD color="white">
              * * * LIMIT OF 3,000 ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF ANY KIND IS
              NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The Rule Breakers
              Tech Giveaway is open only to legal residents of the 50 US States & DC, age of majority +. Void elsewhere
              and where prohibited. Ends 03/09/2025. Subject to complete Official Rules at{' '}
              <a target="_blank" className="underline" href="https://bit.ly/RuleBreakersTech">
                https://bit.ly/RuleBreakersTech
              </a>
              .
            </GFooterMD>
          )}

          {vacationSlugs.has(theatricalSlug) && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="sweepstakesGeneralRulesBraveV3">
                * * * LIMIT OF FIVE THOUSAND (5,000) ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO PURCHASE OR PAYMENT OF
                ANY KIND IS NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR CHANCE OF WINNING. The
                200K Dream Vacation Giveaway is open only to legal residents of the 50 U.S. States &amp; DC, age of
                majority +. Void elsewhere and where prohibited. Promotion ends 3/9/2025. Subject to complete Official
                Rules at https://bit.ly/200KDreamGiveaway.{' '}
                <a href="https://bit.ly/200KDreamGiveaway" target="_blank" className="underline">
                  https://bit.ly/200KDreamGiveaway
                </a>
              </Translate>
            </GFooterMD>
          )}

          {theatricalSlug === slugs.homestead && (
            <GFooterMD color="white">
              <Translate t={t} i18nKey="homesteadSweepstakesGeneralRulesHomesteadV3">
                * * * LIMIT OF FOURTEEN THOUSAND ONE HUNDRED (14,100) ENTRIES PER PERSON/EMAIL ADDRESS. * * * NO
                PURCHASE OR PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN. PURCHASE OR PAYMENT DOES NOT IMPROVE YOUR
                CHANCE OF WINNING. The 300K Instant Homestead Giveaway is open only to legal residents of the 48
                Contiguous U.S. States (VOID in AK and HI) & DC, 21 or older. Void elsewhere and where prohibited..
                Promotion starts 10/11/24 and ends 12/22/24. To enter, visit https://angel.com/homesteadgiveaway .
                Sponsor: Angel Studios, 295 W Center Street, Provo, UT 84601.. Subject to complete Official Rules at{' '}
                <a href="https://bit.ly/HomesteadSweeps" target="_blank" className="underline">
                  https://bit.ly/HomesteadSweeps
                </a>
              </Translate>
            </GFooterMD>
          )}
        </FlexColumn>
      </div>
    </footer>
  )
}
